import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  HandHeart,
  Sparkle,
  Copy,
  CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * SponsorshipProposalModal
 *
 * Premium-only modal that asks the user for an event name + target budget,
 * then calls POST /api/corporate-scout/sponsorship-proposal to generate a
 * 3-tier corporate sponsorship pitch tailored to this scout's CSR priorities.
 */
export const SponsorshipProposalModal = ({ scout, open, onOpenChange }) => {
  const [eventName, setEventName] = useState("");
  const [targetBudget, setTargetBudget] = useState("");
  const [proposal, setProposal] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setEventName("");
    setTargetBudget("");
    setProposal("");
    setCopied(false);
  };

  const generate = async () => {
    if (!eventName.trim()) {
      toast.error("Tell us what you're raising money for");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/corporate-scout/sponsorship-proposal`, {
        company_id: scout.company_id,
        event_name: eventName,
        target_budget: targetBudget,
      });
      setProposal(res.data.proposal || "");
      toast.success("Sponsorship proposal ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't generate proposal");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(proposal);
    setCopied(true);
    toast.success("Proposal copied — paste it into your email");
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent
        className="bg-zinc-950 border-zinc-800 w-[calc(100vw-2rem)] max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="sponsorship-proposal-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandHeart weight="fill" className="w-5 h-5 text-emerald-400" />
            Build Sponsorship Proposal — {scout?.company_name}
          </DialogTitle>
          <p className="text-sm text-zinc-400 mt-1">
            Tell us what you're raising money for. We'll write a 3-tier
            sponsorship proposal tailored to {scout?.company_name}'s documented
            CSR priorities — ready to paste into an email.
          </p>
        </DialogHeader>

        {!proposal ? (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label
                htmlFor="event-name"
                className="text-sm font-semibold text-zinc-300"
              >
                What are you raising money for?
              </Label>
              <Input
                id="event-name"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="e.g., Annual Spring Gala, Summer Reading Program, New Mentor Training"
                className="bg-zinc-900 border-zinc-800"
                data-testid="proposal-event-input"
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="target-budget"
                className="text-sm font-semibold text-zinc-300"
              >
                Target sponsorship budget{" "}
                <span className="text-zinc-500 font-normal">(optional)</span>
              </Label>
              <Input
                id="target-budget"
                value={targetBudget}
                onChange={(e) => setTargetBudget(e.target.value)}
                placeholder="e.g., $5,000"
                className="bg-zinc-900 border-zinc-800"
                data-testid="proposal-budget-input"
              />
              <p className="text-xs text-zinc-500">
                We'll anchor the Gold tier here and scale Bronze/Silver down. Leave blank for $500 / $1,500 / $5,000.
              </p>
            </div>
            <Button
              onClick={generate}
              disabled={loading || !eventName.trim()}
              className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
              data-testid="proposal-generate-btn"
            >
              {loading ? (
                <>
                  <div className="loading-spinner mr-2" />
                  Writing your proposal…
                </>
              ) : (
                <>
                  <Sparkle weight="fill" className="w-4 h-4 mr-2" />
                  Generate Proposal
                </>
              )}
            </Button>
            <p className="text-[11px] text-zinc-600 leading-relaxed">
              The proposal is your pitch to send to {scout?.company_name}. Tier
              benefits are suggestions you can edit before sending. We never
              contact the company on your behalf.
            </p>
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            <div className="bg-zinc-900 p-5 rounded-sm border border-zinc-800">
              <pre
                className="text-sm text-zinc-200 whitespace-pre-wrap font-sans leading-relaxed"
                data-testid="proposal-content"
              >
                {proposal}
              </pre>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={copy}
                className="flex-1 bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                data-testid="proposal-copy-btn"
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Proposal
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={reset}
                className="border-zinc-700 rounded-sm"
                data-testid="proposal-regenerate-btn"
              >
                Start Over
              </Button>
            </div>
            <p className="text-[11px] text-zinc-600">
              Edit before sending. Replace any [INSERT: ...] placeholders with
              your real details, then paste into your email or pitch deck.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SponsorshipProposalModal;
